import { FormControlLabel, FormHelperText, Radio } from '@mui/material';
import { getIn } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';

import { LabelText } from '@styles/StyledTypography';

import { RelativeWrapper } from '../styledComponents';
import { ICheckboxListFieldProps } from './CheckboxListField';

type RadioButtonValue = string | number | boolean;

export const RadioButtonListField = ({
  field,
  form,
  disabled: _disabled,
  options,
  checked,
  nextLine = false,
  onChange,
  ...props
}: ICheckboxListFieldProps) => {
  const error = getIn(form.errors, field.name);
  const touched = getIn(form.touched, field.name);

  useEffect(() => {
    if (checked && !field.value) {
      form.setFieldValue(field.name, checked);
    }
  }, [checked, field.name, field.value, form]);

  const isChecked = useCallback((value: unknown) => field?.value === value, [field]);

  const internalOnChange = useCallback(
    (value: RadioButtonValue) => { // Use RadioButtonValue
      form.setFieldTouched(field.name);
      form.setFieldValue(field.name, value);
      if (onChange) {
        onChange(String(value));
      }
    },
    [form, field.name, onChange],
  );

  const labelSx = useMemo(() => (nextLine ? { width: '100%', height: 'auto' } : {}), [nextLine]);

  return (
    <RelativeWrapper fullHeight>
      {options?.map((option, index) => (
        <FormControlLabel
          key={index}
          label={<LabelText wrap>{option.label}</LabelText>}
          sx={labelSx}
          control={
            <Radio
              {...props}
              name={option?.name || option.label}
              checked={isChecked(option.value)}
              onChange={() => internalOnChange(option.value as RadioButtonValue)}
            />
          }
        />
      ))}

      {error && touched && <FormHelperText error>{error}</FormHelperText>}
    </RelativeWrapper>
  );
};
